import React, { useEffect, useRef, useState } from 'react';
import {
  CondensedHeader,
  ExperiencePlaceholderIcon,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
} from '@axiom/ui';
import type { Experience, Taxonomy } from '@axiom/validation';

import NoContentPlaceholder from '../NoContentPlaceholder/NoContentPlaceholder';
import { formatDataTestId } from '../../utils/data-test-util';
import { WindowUtil } from '../../utils/window-util';

import { CandidateExperienceUtil } from './candidate-experience-util';
import { CandidateExperienceItem } from './CandidateExperienceItem';

export type CandidateExperienceListProps = {
  name?: string;
  canAddNew?: boolean;
  experiences: Experience[];
  itemToScrollIntoView?: Experience;
  onAddExperience?: () => void;
  onEditExperience?: (experience: Experience) => void;
  talentName: string | null;
  taxonomy: Taxonomy;
  title: React.ReactNode;
};

type ExpandedState = Record<string, boolean>;

export const CandidateExperienceList = ({
  name,
  canAddNew = false,
  experiences,
  itemToScrollIntoView,
  onAddExperience,
  onEditExperience,
  talentName,
  taxonomy,
  title,
}: CandidateExperienceListProps) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [expandedAll, setExpandedAll] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const scrollIntoViewRef = useRef<HTMLDivElement>(null);

  // Initialize expanded state on mount and when experiences change
  useEffect(() => {
    const query = WindowUtil.getUrlQuery();
    const shouldScrollToException = !!query.scrollToExceptionRequest;

    setExpanded(prev => {
      const newExpanded = experiences.reduce<ExpandedState>((acc, curr) => {
        acc[curr.id] =
          prev[curr.id] ??
          !!(curr.exceptionRequestedAt && !curr.exceptionApprovedAt);
        return acc;
      }, {});

      setExpandedAll(
        experiences.length > 0 &&
          experiences.every(e => newExpanded[e.id] === true)
      );
      return newExpanded;
    });
    setShouldScroll(shouldScrollToException);
  }, [experiences]);

  // Expand experiences that match search query
  useEffect(() => {
    const query = WindowUtil.getUrlQuery();
    if (query.search && typeof query.search === 'string') {
      const searchExpanded = experiences.reduce<ExpandedState>(
        (acc, experience) => {
          if (
            CandidateExperienceUtil.doesExperienceContainSearch(
              experience,
              query.search as string
            )
          ) {
            acc[experience.id] = true;
          }
          return acc;
        },
        {}
      );

      setExpanded(prev => {
        const newExpanded = { ...prev, ...searchExpanded };
        setExpandedAll(
          experiences.length > 0 &&
            experiences.every(e => newExpanded[e.id] === true)
        );
        return newExpanded;
      });
    }
  }, [experiences]);

  // Handle scroll to exception request
  useEffect(() => {
    if (scrollIntoViewRef.current && shouldScroll) {
      scrollIntoViewRef.current.scrollIntoView({
        behavior: 'smooth',
      });
      setShouldScroll(false);

      const query = WindowUtil.getUrlQuery();
      delete query.scrollToExceptionRequest;
      WindowUtil.setUrlQuery(query, true);
    }
  }, [shouldScroll]);

  const openAddExperienceForm = () => {
    onAddExperience?.();
  };

  const toggleAllExpanded = () => {
    const newExpandedAll = experiences.length > 0 && !expandedAll;
    const newExpanded = experiences.reduce<ExpandedState>((acc, experience) => {
      acc[experience.id] = newExpandedAll;
      return acc;
    }, {});

    setExpanded(newExpanded);
    setExpandedAll(newExpandedAll);
  };

  return (
    <Gutter
      name={name}
      top="32px"
      right="16px"
      left="16px"
      data-test={formatDataTestId(title)}
    >
      <Layout direction="horizontal" position="space-between middle">
        <LayoutItem>
          <CondensedHeader name="EXPERIENCE_LIST_TITLE">
            {title}
          </CondensedHeader>
        </LayoutItem>
        <LayoutItem>
          <Layout direction="horizontal" horizontalGutter="8px">
            {canAddNew ? (
              <LayoutItem>
                <IconButton
                  icon="plus"
                  pattern="secondary"
                  variation="minimal"
                  name="PRIOREXPERIENCEPLUSICON"
                  onClick={openAddExperienceForm}
                />
              </LayoutItem>
            ) : null}
            <LayoutItem>
              <IconButton
                icon={expandedAll ? 'collapse-all' : 'expand-all'}
                pattern="secondary"
                variation="minimal"
                name="EXPANDALLBUTTON"
                onClick={toggleAllExpanded}
              />
            </LayoutItem>
          </Layout>
        </LayoutItem>
      </Layout>

      {experiences.length > 0 ? (
        <div>
          {experiences.map(experience => (
            <div
              key={experience.id}
              ref={
                itemToScrollIntoView?.id === experience.id
                  ? scrollIntoViewRef
                  : null
              }
            >
              <CandidateExperienceItem
                expanded={expanded[experience.id]}
                experience={experience}
                talentName={talentName || ''}
                taxonomy={taxonomy}
                onEditExperience={onEditExperience}
                onToggleExpanded={(isExpanded: boolean) => {
                  setExpanded(prev => {
                    const newExpanded = {
                      ...prev,
                      [experience.id]: isExpanded,
                    };
                    setExpandedAll(
                      experiences.length > 0 &&
                        experiences.every(e => newExpanded[e.id] === true)
                    );
                    return newExpanded;
                  });
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <NoContentPlaceholder
          image={<ExperiencePlaceholderIcon />}
          title="No Experience"
          text={
            canAddNew ? 'Press the add button to enter other experience' : ''
          }
        />
      )}

      <Gutter bottom="64px" />
    </Gutter>
  );
};
